import Items from "@/src/interfaces/catalog/items"
import {
    CACHE_EXPIRATION_MS,
    CATEGORIES_STORE_NAME,
    ITEMS_STORE_NAME,
    LIST_ITEMS_STORE_NAME,
    PROMOTIONS_STORE_NAME,
    STATUS_STORE_NAME,
} from "@/src/constants/defaults"
import { ItemService } from "@/src/services/catalog/item-service"
import { CategoryService } from "@/src/services/catalog/category-service"
import Category from "@/src/interfaces/catalog/category"
import { MerchantBenefitsService } from "@/src/services/benefits/merchant-service"
import MerchantBenefits from "@/src/interfaces/benefits/merchant"
import { MerchantPollingService } from "@/src/services/polling/merchant-service"
import MerchantPolling from "@/src/interfaces/polling/merchant"
import CacheHelper from "./cache-helper"

const cacheErr = 'Erro ao acessar cache do IndexedDB:'

export default class CacheHandler {
    private static isExpired = (cached: any) => {
        const now = Date.now()

        if (cached.timestamp && cached.data && now - cached.timestamp > CACHE_EXPIRATION_MS)
            return true

        return false
    }

    private static getSaveErrMsg = (name: string) => {
        return `Erro ao salvar ${name} no IndexedDB:`
    }

    public static async getCategories(
        param: Category.SearchParams,
        appAccessToken?: string
    ) {
        const db = await CacheHelper.getCacheDB()

        if (!db)
            return

        const cacheKey = CacheHelper.generateCacheKey(CATEGORIES_STORE_NAME)

        try {
            await CacheHelper.cleanupExpiredCaches(CATEGORIES_STORE_NAME)
            const cached = await db.get(CATEGORIES_STORE_NAME, cacheKey)

            if (cached) {
                if (this.isExpired(cached))
                    await db.delete(CATEGORIES_STORE_NAME, cacheKey)
                else
                    return cached.data as Category.Category[]
            }
        }
        catch (err) {
            console.warn(cacheErr, err)
        }

        const response = await CategoryService.getAll(param, appAccessToken)

        if (!response.success) {
            console.error(response.errorMessage)
            return undefined
        }

        const data = response.model?.items || []

        try {
            await db.put(CATEGORIES_STORE_NAME, {
                data,
                timestamp: Date.now(),
            }, cacheKey)
        }
        catch (err) {
            console.warn(this.getSaveErrMsg('categorias'), err)
        }

        return data
    }

    public static async getItems(
        param: Items.ItemsSearchParams,
        appAccessToken?: string
    ) {
        const db = await CacheHelper.getCacheDB()

        if (!db)
            return

        const cacheKey = CacheHelper.generateCacheKey(ITEMS_STORE_NAME, param.categoryId)

        try {
            await CacheHelper.cleanupExpiredCaches(ITEMS_STORE_NAME)
            const cached = await db.get(ITEMS_STORE_NAME, cacheKey)

            if (cached) {
                if (this.isExpired(cached))
                    await db.delete(ITEMS_STORE_NAME, cacheKey)
                else
                    return cached.data as Items.ItemResponse[]
            }
        }
        catch (err) {
            console.warn(cacheErr, err)
        }

        const response = await ItemService.getItemsSummary(param, appAccessToken)

        if (!response.success) {
            console.error(response.errorMessage)
            return undefined
        }

        const data = response.model?.items || []

        try {
            await db.put(ITEMS_STORE_NAME, {
                data,
                timestamp: Date.now(),
            }, cacheKey)
        }
        catch (err) {
            console.warn(this.getSaveErrMsg('itens'), err)
        }

        return data
    }

    public static async getPromotions(
        param: MerchantBenefits.SearchParams,
        appAccessToken?: string
    ) {
        const db = await CacheHelper.getCacheDB()

        if (!db)
            return

        const cacheKey = CacheHelper.generateCacheKey(PROMOTIONS_STORE_NAME)

        try {
            await CacheHelper.cleanupExpiredCaches(PROMOTIONS_STORE_NAME)
            const cached = await db.get(PROMOTIONS_STORE_NAME, cacheKey)

            if (cached) {
                if (this.isExpired(cached))
                    await db.delete(PROMOTIONS_STORE_NAME, cacheKey)
                else
                    return cached.data as MerchantBenefits.PromotionResponse
            }
        }
        catch (err) {
            console.warn(cacheErr, err)
        }

        const response = await MerchantBenefitsService.getPromotions(param, appAccessToken)

        if (!response.success) {
            console.error(response.errorMessage)
            return undefined
        }

        const data = response.model

        try {
            if (data) {
                await db.put(PROMOTIONS_STORE_NAME, {
                    data,
                    timestamp: Date.now(),
                }, cacheKey)
            }
        }
        catch (err) {
            console.warn(this.getSaveErrMsg('promoções'), err)
        }

        return data
    }

    public static async getListItems(
        merchantId: string,
        idsList: string[],
        localMode: boolean,
        appAccessToken?: string
    ) {
        const db = await CacheHelper.getCacheDB()

        if (!db)
            return

        const cacheKey = CacheHelper.generateCacheKey(LIST_ITEMS_STORE_NAME)

        try {
            await CacheHelper.cleanupExpiredCaches(LIST_ITEMS_STORE_NAME)
            const cached = await db.get(LIST_ITEMS_STORE_NAME, cacheKey)

            if (cached) {
                if (this.isExpired(cached))
                    await db.delete(LIST_ITEMS_STORE_NAME, cacheKey)
                else
                    return cached.data as Items.ItemResponse[]
            }
        }
        catch (err) {
            console.warn(cacheErr, err)
        }

        const response = await ItemService.getListSummary(merchantId, idsList, localMode, appAccessToken)

        if (!response.success) {
            console.error(response.errorMessage)
            return undefined
        }

        const data = response.model?.items || []

        try {
            await db.put(LIST_ITEMS_STORE_NAME, {
                data,
                timestamp: Date.now(),
            }, cacheKey)
        }
        catch (err) {
            console.warn(this.getSaveErrMsg('lista de itens'), err)
        }

        return data
    }

    public static async getStatus(
        merchantId: string,
        appAccessToken?: string
    ) {
        const db = await CacheHelper.getCacheDB()

        if (!db)
            return

        const cacheKey = CacheHelper.generateCacheKey(STATUS_STORE_NAME)

        try {
            await CacheHelper.cleanupExpiredCaches(STATUS_STORE_NAME)
            const cached = await db.get(STATUS_STORE_NAME, cacheKey)

            if (cached) {
                if (this.isExpired(cached))
                    await db.delete(STATUS_STORE_NAME, cacheKey)
                else
                    return cached.data as MerchantPolling.StatusResponse
            }
        }
        catch (err) {
            console.warn(cacheErr, err)
        }

        const response = await MerchantPollingService.getStatus(merchantId, appAccessToken)

        if (!response.success) {
            console.error(response.errorMessage)
            return undefined
        }

        const data = response.model

        try {
            if (data) {
                await db.put(STATUS_STORE_NAME, {
                    data,
                    timestamp: Date.now(),
                }, cacheKey)
            }
        }
        catch (err) {
            console.warn(this.getSaveErrMsg('status'), err)
        }

        return data
    }
}